import { ReturnReceiptItemRes } from '@/services/return/returnReceipt/types'

/** 수령 스캔 판정 4종 — moms-be `ReturnReceiptReceiveService` 상수와 동일 문자열. */
export const SCAN_STATUS = {
  SUCCESS: '성공',
  MISS: '오입고',
  UNREGISTERED: '미등록',
  DUPLICATE: '중복스캔',
} as const

export type ScanStatus = (typeof SCAN_STATUS)[keyof typeof SCAN_STATUS]

/** `POST /api/v1/return/receipts` 요청 — 화면이 채우는 값만 든다. MENU_CD·USR_ID 조립은 apis.ts 가 한다. */
export interface ReceiveScanReq {
  centerNm: string
  rtnInvoiceNo: string
  /** yyyy-MM-dd(ISO). 공란·미지정이면 서버가 오늘로 대체한다. 압축형(yyyyMMdd) 변환은 apis.ts 가 한다. */
  assignDate?: string
  usrId: string
}

/**
 * `rtnData` 행 — 1.0 `UI_RETURN_INVOICE` DB 컬럼명 그대로다(moms-be `ReceiveReturnReceiptRow`).
 * 화면이 쓰지 않는 필드(ASSIGN_DATE·ORD_REMARK·SRC_TABLE_ID·SRC_TABLE_NM)는 선언하지 않는다.
 *
 * `SCAN_DATE` 는 표에 그리지 않지만 선언한다 — 이력 조회 결과와 방금 스캔한 행을 합칠 때
 * 같은 건인지 가리는 식별자다([mergeScanRows]).
 */
export interface ReceiveScanRowRes {
  id: number | null
  SPR_NM: string | null
  CLS_DATE: string | null
  /** `yyyyMMddHHmmss`. 스캔 1회당 하나라 반송번호와 묶으면 행 식별자가 된다. */
  SCAN_DATE: string | null
  RTN_INVOICE_NO: string | null
  WH_NM: string | null
  SCAN_STATUS: string
  INS_DATE: string | null
  INS_BY: string | null
  UPD_DATE: string | null
  UPD_BY: string | null
  GI_WH_NM: string | null
}

/**
 * 응답. HTTP 는 항상 200 이고 분기는 `rtnChk` 로 한다 — "판정 처리가 정상 수행됐는가"이지
 * "정상 입고인가"가 아니다(오입고·미등록·중복스캔도 true). `rtnData` 는 rtnChk=true 면 항상 1건,
 * false 면 빈 배열이다(moms-be `ReceiveReturnReceiptResponseBody.of`/`failure`).
 * `message` 는 성공 판정이면 키 자체가 없다.
 */
export interface ReceiveScanRes {
  rtnChk: boolean
  rtnData: ReceiveScanRowRes[]
  MENU_CD: string
  message?: string
}

/**
 * 화면 표 행 — columnDefinitions 의 `dataIndex`(=생성 파일 dataIndex)와 1:1.
 * `key`·`SCAN_DATE` 만 예외로, 컬럼이 없고 React key 와 행 식별에만 쓴다.
 */
export interface ScanResultRow {
  key: string
  /** 행 식별용 원본 스캔시각(`yyyyMMddHHmmss`). 표에 그리지 않는다. */
  SCAN_DATE: string
  SPR_CD: string
  CLOSE_DATE: string
  RTN_INVOICE_NO: string
  IN_CENTER: string
  SCAN_STATUS: ScanStatus
  INS_DATE: string
  INS_BY: string
  UPD_DATE: string
  UPD_BY: string
  GI_WH_NM: string
}

/**
 * `yyyyMMdd`·`yyyyMMddHHmmss`(압축형) → `yyyy-MM-dd`. 시각은 버린다(생성자·수정자 칸도 데모가 날짜만 표시).
 * 레포에 dayjs customParseFormat 플러그인이 없어(`stockMoveHistory/searchQuery.ts` 선례) 슬라이싱으로 변환한다.
 * 압축형(숫자 8자리 이상)인지 먼저 확인한다 — moms-be 계약상 이 형식만 오지만, ISO(`yyyy-MM-dd`)가 섞여 들어와도
 * 슬라이싱으로 깨진 문자열을 만들지 않고 원본을 그대로 돌려준다.
 */
export const toDisplayDate = (value: string | null): string => {
  if (!value || !/^\d{8}/.test(value)) return value ?? ''
  return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`
}

/** `id` 가 없을 때(이론상 미도달) React key 폴백 — 밀리초 내 연속 스캔에서도 겹치지 않게 단조 증가 카운터를 쓴다. */
let fallbackKeyCounter = 0

/** 응답 행 → 화면 행. `id` 가 없으면(이론상 미도달) 반송번호+폴백 카운터로 대체한다. */
export const toScanResultRow = (row: ReceiveScanRowRes): ScanResultRow => ({
  key: row.id == null ? `${row.RTN_INVOICE_NO ?? 'unknown'}-${fallbackKeyCounter++}` : String(row.id),
  SCAN_DATE: row.SCAN_DATE ?? '',
  SPR_CD: row.SPR_NM ?? '',
  CLOSE_DATE: toDisplayDate(row.CLS_DATE),
  RTN_INVOICE_NO: row.RTN_INVOICE_NO ?? '',
  IN_CENTER: row.WH_NM ?? '',
  // 서버 계약상 SCAN_STATUS 는 SCAN_STATUS 상수 4종 중 하나만 온다 — 단언은 방어적 타입 가드보다 실용적이다.
  SCAN_STATUS: row.SCAN_STATUS as ScanStatus,
  INS_DATE: toDisplayDate(row.INS_DATE),
  INS_BY: row.INS_BY ?? '',
  UPD_DATE: toDisplayDate(row.UPD_DATE),
  UPD_BY: row.UPD_BY ?? '',
  GI_WH_NM: row.GI_WH_NM ?? '',
})

/**
 * 이력 조회(`GET /api/v1/return/receipts`) 응답 행 → 같은 화면 행. (LOGISPRD-12610)
 *
 * 스캔 응답과 달리 `id` 가 계약에 없어(moms-be `ReturnReceiptItemDto`) 반송번호+스캔시각을 key 로 쓴다.
 * 같은 반송번호를 같은 초에 두 번 기록하는 경로가 없어 이 조합이 곧 행 하나다.
 *
 * `SCAN_DATE` 는 실제로 비어 올 수 있다. 조회 where 절은 `SCAN_STATUS` 만 거르고 `SCAN_DATE` 에는
 * 아무 술어도 걸지 않으며(moms-be `ReturnReceiptJpaRepository.commonPredicate` — 투영에만 등장),
 * 컬럼도 varchar(18) NULL 이다. dev 실측으로 조회 조건 통과분 668건 중 117건이 NULL 이다(전부 2023년
 * 레거시, 2024년 이후 0건). 값은 원본 그대로 두고(빈 값은 빈 문자열) 뭉침 방지는 [rowIdentity] 가 맡는다.
 */
export const toScanResultRowFromItem = (row: ReturnReceiptItemRes): ScanResultRow => {
  const key = `${row.rtnInvoiceNo ?? 'unknown'}-${row.scanDate ?? fallbackKeyCounter++}`
  return {
    key,
    SCAN_DATE: row.scanDate ?? '',
    SPR_CD: row.sprNm ?? '',
    CLOSE_DATE: toDisplayDate(row.clsDate),
    RTN_INVOICE_NO: row.rtnInvoiceNo ?? '',
    IN_CENTER: row.whNm ?? '',
    SCAN_STATUS: row.scanStatus as ScanStatus,
    INS_DATE: toDisplayDate(row.insDate),
    INS_BY: row.insBy ?? '',
    UPD_DATE: toDisplayDate(row.updDate),
    UPD_BY: row.updBy ?? '',
    GI_WH_NM: row.giWhNm ?? '',
  }
}

/**
 * 행 식별자 — 출처(스캔 응답·이력 조회)가 달라도 같은 건이면 같은 값이 나온다.
 *
 * 스캔시각이 비면 반송번호만 남아 같은 반송번호의 여러 행이 한 값으로 뭉친다. 지금 [mergeScanRows] 는
 * 이력끼리는 비교하지 않고 스캔 행은 스캔시각이 항상 차 있어(moms-be 가 스캔마다 새 행에 `nowStr` 를
 * 적는다) 실제로 겹치지는 않지만, 그 두 전제가 바뀌면 조용히 행이 사라진다. 빈 값이면 행마다 다른
 * `key` 로 갈라 전제와 무관하게 안전하게 둔다.
 */
const rowIdentity = (row: ScanResultRow): string =>
  row.SCAN_DATE ? `${row.RTN_INVOICE_NO}|${row.SCAN_DATE}` : `key|${row.key}`

/**
 * 이번 세션에서 스캔한 행을 이력 조회 결과 앞에 붙인다. (LOGISPRD-12610)
 *
 * 스캔 직후에는 이력을 다시 부르지 않으므로 평소에는 겹치지 않는다. 조회조건을 되돌리거나 화면이
 * 다시 마운트돼 이력이 새로 오면 방금 스캔한 건이 양쪽에 들어오는데, 그때는 스캔 쪽을 남긴다 —
 * 화면에 이미 그려져 있던 행이다.
 */
export const mergeScanRows = (scanned: ScanResultRow[], history: ScanResultRow[]): ScanResultRow[] => {
  const scannedIdentities = new Set(scanned.map(rowIdentity))
  return [...scanned, ...history.filter((row) => !scannedIdentities.has(rowIdentity(row)))]
}
